package fr.manyverse.app.presentation.components.player_screen.character

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.MenuBook
import androidx.compose.material.icons.filled.Favorite
import androidx.compose.material.icons.filled.Shield
import androidx.compose.material.icons.filled.Whatshot
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedCard
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.drawBehind
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.PathEffect
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import fr.manyverse.app.domain.model.CharacterSheet
import fr.manyverse.app.domain.model.CharacteristicEntry
import fr.manyverse.app.domain.model.GaugeEntry
import fr.manyverse.app.domain.model.PowerEntry
import fr.manyverse.app.domain.model.SkillEntry
import fr.manyverse.app.presentation.components.character_sheet.ExpandableDescription
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.character_sheet_characteristics_section
import manyverse.shared.generated.resources.character_sheet_gauges_section
import manyverse.shared.generated.resources.character_sheet_powers_section
import manyverse.shared.generated.resources.character_sheet_skills_section
import org.jetbrains.compose.resources.stringResource

/**
 * Affichage en lecture seule d'une [CharacterSheet], au style identique à la fiche du joueur
 * ([CharacterSheetTab] hors mode édition), mais sans aucune interaction.
 *
 * Utilisé pour montrer aux joueurs les caractéristiques d'un pré-tiré défini par le MJ avant
 * qu'ils ne le choisissent. Les sections vides sont masquées.
 */
@Composable
fun CharacterSheetReadOnly(
    sheet: CharacterSheet,
    modifier: Modifier = Modifier
) {
    val isEmpty = sheet.gauges.isEmpty() &&
        sheet.characteristics.isEmpty() &&
        sheet.skills.isEmpty() &&
        sheet.powers.isEmpty()
    if (isEmpty) return

    Column(
        modifier = modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(16.dp)
    ) {
        if (sheet.gauges.isNotEmpty()) {
            ReadOnlySheetSection(
                icon = Icons.Default.Favorite,
                title = stringResource(Res.string.character_sheet_gauges_section)
            ) {
                sheet.gauges.forEach { ReadOnlyGaugeRow(it) }
            }
        }

        if (sheet.characteristics.isNotEmpty()) {
            ReadOnlySheetSection(
                icon = Icons.Default.Shield,
                title = stringResource(Res.string.character_sheet_characteristics_section)
            ) {
                @OptIn(ExperimentalLayoutApi::class)
                FlowRow(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                    verticalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    sheet.characteristics.forEach { characteristic ->
                        ReadOnlyCharacteristicCell(
                            characteristic = characteristic,
                            modifier = Modifier.widthIn(min = 140.dp).weight(1f)
                        )
                    }
                }
            }
        }

        if (sheet.skills.isNotEmpty()) {
            ReadOnlySheetSection(
                icon = Icons.AutoMirrored.Filled.MenuBook,
                title = stringResource(Res.string.character_sheet_skills_section)
            ) {
                sheet.skills.forEachIndexed { index, skill ->
                    ReadOnlySkillRow(skill)
                    if (index < sheet.skills.lastIndex) {
                        HorizontalDivider(
                            color = MaterialTheme.colorScheme.outline.copy(alpha = 0.2f)
                        )
                    }
                }
            }
        }

        if (sheet.powers.isNotEmpty()) {
            ReadOnlySheetSection(
                icon = Icons.Default.Whatshot,
                title = stringResource(Res.string.character_sheet_powers_section)
            ) {
                sheet.powers.forEachIndexed { index, power ->
                    ReadOnlyPowerRow(power)
                    if (index < sheet.powers.lastIndex) {
                        HorizontalDivider(
                            color = MaterialTheme.colorScheme.outline.copy(alpha = 0.2f)
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun ReadOnlySheetSection(
    icon: ImageVector,
    title: String,
    content: @Composable ColumnScope.() -> Unit
) {
    OutlinedCard(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(12.dp)
    ) {
        Column(
            modifier = Modifier.padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(10.dp)
            ) {
                Icon(
                    imageVector = icon,
                    contentDescription = null,
                    tint = MaterialTheme.colorScheme.primary,
                    modifier = Modifier.size(20.dp)
                )
                Text(
                    text = title.uppercase(),
                    style = MaterialTheme.typography.labelLarge,
                    fontWeight = FontWeight.SemiBold,
                    letterSpacing = 1.5.sp,
                    color = MaterialTheme.colorScheme.primary
                )
                Box(
                    modifier = Modifier
                        .weight(1f)
                        .height(1.dp)
                        .background(
                            Brush.horizontalGradient(
                                listOf(
                                    MaterialTheme.colorScheme.primary.copy(alpha = 0.3f),
                                    Color.Transparent
                                )
                            )
                        )
                )
            }
            content()
        }
    }
}

@Composable
private fun ReadOnlyGaugeRow(gauge: GaugeEntry) {
    val span = gauge.max - gauge.min
    val fraction = if (span > 0) {
        ((gauge.current - gauge.min).toFloat() / span).coerceIn(0f, 1f)
    } else {
        0f
    }
    val gaugeColor = gauge.colorArgb?.let { Color(it) } ?: MaterialTheme.colorScheme.primary

    Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween
        ) {
            Text(
                text = gauge.label,
                style = MaterialTheme.typography.bodyLarge,
                modifier = Modifier.weight(1f)
            )
            Row(verticalAlignment = Alignment.Bottom) {
                Text(
                    text = gauge.current.toString(),
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.Bold,
                    color = MaterialTheme.colorScheme.onSurface
                )
                Text(
                    text = " / ${gauge.max}",
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
        }
        LinearProgressIndicator(
            progress = { fraction },
            modifier = Modifier
                .fillMaxWidth()
                .height(8.dp)
                .clip(RoundedCornerShape(4.dp)),
            color = gaugeColor,
            trackColor = MaterialTheme.colorScheme.surfaceVariant
        )
    }
}

@Composable
private fun ReadOnlyCharacteristicCell(
    characteristic: CharacteristicEntry,
    modifier: Modifier = Modifier
) {
    Column(modifier = modifier.padding(vertical = 6.dp)) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            Text(
                text = characteristic.label,
                style = MaterialTheme.typography.bodyLarge,
                color = MaterialTheme.colorScheme.onSurface
            )
            DottedLeader(modifier = Modifier.weight(1f))
            Text(
                text = characteristic.value.toString(),
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.SemiBold,
                color = MaterialTheme.colorScheme.primary
            )
        }
        ExpandableDescription(characteristic.description)
    }
}

@Composable
private fun ReadOnlySkillRow(skill: SkillEntry) {
    Column(modifier = Modifier.fillMaxWidth().padding(vertical = 4.dp)) {
        Text(
            text = skill.label,
            style = MaterialTheme.typography.bodyLarge,
            modifier = Modifier.fillMaxWidth()
        )
        ExpandableDescription(skill.description)
    }
}

@Composable
private fun ReadOnlyPowerRow(power: PowerEntry) {
    Column(modifier = Modifier.fillMaxWidth().padding(vertical = 4.dp)) {
        Text(
            text = power.label,
            style = MaterialTheme.typography.bodyLarge,
            modifier = Modifier.fillMaxWidth()
        )
        ExpandableDescription(power.description)
    }
}

/** Filet de points reliant le libellé à la valeur, comme sur la fiche du joueur. */
@Composable
private fun DottedLeader(modifier: Modifier = Modifier) {
    val color = MaterialTheme.colorScheme.outline.copy(alpha = 0.5f)
    Box(
        modifier = modifier
            .height(1.dp)
            .drawBehind {
                val y = size.height / 2f
                drawLine(
                    color = color,
                    start = Offset(0f, y),
                    end = Offset(size.width, y),
                    strokeWidth = 1.5.dp.toPx(),
                    cap = StrokeCap.Round,
                    pathEffect = PathEffect.dashPathEffect(
                        floatArrayOf(1f, 6.dp.toPx()),
                        0f
                    )
                )
            }
    )
}
